import React from 'react';
import FreshnessIndicator from './FreshnessIndicator';
import SeeFilesButton from './SeeFilesButton';
import { getDriveFolderUrl } from './driveFolders';

// Map investment types to a consistent accent color
const TYPE_COLORS = {
  Stocks:    '#3B82F6',
  Bonds:     '#10B981',
  Cash:      '#F59E0B',
  Crypto:    '#8B5CF6',
  'Real Estate': '#06B6D4',
  PE:        '#EF4444',
  Other:     '#6B7280',
};

function getTypeColor(type) {
  return TYPE_COLORS[type] || '#667eea';
}

// Compute 30-day trend from valueHistory
function computeTrend(investment) {
  const history = investment.valueHistory;
  if (!history || history.length < 2) return null;

  const sorted = [...history].sort((a, b) => new Date(a.timeOfUpdate) - new Date(b.timeOfUpdate));
  const now = sorted[sorted.length - 1];
  const cutoff = new Date(now.timeOfUpdate);
  cutoff.setDate(cutoff.getDate() - 30);

  const past = sorted.filter(h => new Date(h.timeOfUpdate) <= cutoff).slice(-1)[0];
  if (!past || past.eurValue === 0) return null;

  const pct = ((now.eurValue - past.eurValue) / past.eurValue) * 100;
  return pct;
}

// Auto-updated investment names (driven by cron jobs)
const AUTO_UPDATED_LIST = ['Brutus', 'Brutus & Co BV', 'GryKRYCA', 'GryKriCa', 'GryKriCa Comm.V', 'Kx Invest', 'KX Invest', 'KX Invest BV', 'Fidelity', 'Fidelity GS', 'Fidelity ISA', 'Interactive Brokers', 'Monarch Assurance', 'Marc Junior NV', 'ING'];
// Multi-user: any 'Saxo *' card (Saxo Kyra, Saxo Mama, ...) is auto-updated by cron
const isAutoUpdatedName = (n) => AUTO_UPDATED_LIST.includes(n) || (typeof n === 'string' && n.startsWith('Saxo'));

// Investments that open a detail popup when clicked
const POPUP_LIST = ['Brutus', 'Brutus & Co BV', 'GryKRYCA', 'GryKriCa', 'GryKriCa Comm.V', 'Kx Invest', 'KX Invest', 'KX Invest BV', 'Fidelity', 'Fidelity ISA', 'Fidelity GS', 'Interactive Brokers', 'Monarch Assurance', 'Huis', 'Patrimonia', 'FOM', 'EverBlue', 'GS StoneBridge', 'Sourdough Sophia', 'Marc Junior', 'Marc Junior NV'];
// Multi-user: any 'Saxo *' card opens the SaxoPopup
const isPopupInvestment = (n) => POPUP_LIST.includes(n) || (typeof n === 'string' && n.startsWith('Saxo'));

function InvestmentItem({
  investment,
  editableInvestment,
  editMode,
  updatedInvestment,
  fxRate,
  handleEditChange,
  handleAmend,
  handleUpdateValue,
  handleCancel,
  handleUpdate,
  handleInvestmentClick,
}) {
  const isEditable = editableInvestment === investment._id && editMode === 'amend';
  const typeColor = getTypeColor(investment.type);
  const trend = computeTrend(investment);
  const isAutoUpdated = isAutoUpdatedName(investment.name);
  const driveUrl = getDriveFolderUrl(investment.name);

  return (
    <div
      className="investment-item"
      style={{ borderLeft: `4px solid ${typeColor}` }}
    >
      {/* ── Header row ── */}
      <div className="inv-header">
        <h3
          className={isPopupInvestment(investment.name) ? 'clickable' : ''}
          onClick={isPopupInvestment(investment.name)
            ? () => handleInvestmentClick(investment.name)
            : null}
        >
          {isEditable ? (
            <input
              type="text"
              name="name"
              value={updatedInvestment.name}
              onChange={handleEditChange}
              className="edit-input"
            />
          ) : (
            investment.name
          )}
        </h3>
        <span className="inv-type-badge" style={{ background: `${typeColor}18`, color: typeColor }}>
          {isEditable ? (
            <input
              type="text"
              name="type"
              value={updatedInvestment.type}
              onChange={handleEditChange}
              className="edit-input edit-input--inline"
            />
          ) : (
            investment.type
          )}
        </span>
        {!isEditable && driveUrl && (
          <SeeFilesButton url={driveUrl} variant="icon" />
        )}
      </div>

      {/* ── Sub-row: subType + freshness ── */}
      <div className="inv-subrow">
        <span className="inv-subtype">
          {isEditable ? (
            <input
              type="text"
              name="subType"
              value={updatedInvestment.subType}
              onChange={handleEditChange}
              className="edit-input edit-input--inline"
              placeholder="SubType"
            />
          ) : (
            investment.subType || ''
          )}
        </span>
        <FreshnessIndicator
          timeOfUpdate={investment.timeOfUpdate}
          updateSource={isAutoUpdated ? 'auto' : 'manual'}
        />
      </div>

      {/* ── Info row ── */}
      {isEditable ? (
        <div className="detail-row">
          <label>Info:</label>
          <input
            type="text"
            name="info"
            value={updatedInvestment.info}
            onChange={handleEditChange}
            className="edit-input"
          />
        </div>
      ) : investment.info ? (
        <div className="inv-info">{investment.info}</div>
      ) : null}

      {/* ── Value section ── */}
      <div className="inv-value-block">
        <div className="inv-eur-value">
          €{Math.round(investment.eurValue).toLocaleString()}
        </div>
        <div className="inv-native-value">
          {editableInvestment === investment._id && editMode === 'updateValue' ? (
            <input
              type="text"
              name="value"
              value={updatedInvestment.value}
              onChange={handleEditChange}
              className="edit-input"
            />
          ) : (
            <span>{investment.currency !== 'EUR' ? `${investment.currency} ${investment.value.toLocaleString()}` : ''}</span>
          )}
        </div>

        {/* FX rate preview during updateValue edit */}
        {editableInvestment === investment._id && fxRate && (
          <div className="detail-row detail-row--compact">
            <label>FX:</label>
            <span>{fxRate}</span>
            <label style={{ marginLeft: 12 }}>EUR:</label>
            <span>€{Math.round(updatedInvestment.value * fxRate).toLocaleString()}</span>
          </div>
        )}

        {/* 30-day trend */}
        {trend !== null && (
          <div className={`inv-trend ${trend >= 0 ? 'inv-trend--up' : 'inv-trend--down'}`}>
            {trend >= 0 ? '▲' : '▼'} {Math.abs(trend).toFixed(1)}% (30d)
          </div>
        )}
      </div>

      {/* ── Actions ── */}
      <div className="investment-buttons">
        {editableInvestment === investment._id ? (
          <>
            <button onClick={handleCancel} className="cancel-button">Cancel</button>
            <button onClick={() => handleUpdate(investment._id)} className="update-button">Save</button>
          </>
        ) : (
          <>
            <button
              onClick={() => handleAmend(investment)}
              disabled={editableInvestment !== null && editableInvestment !== investment._id}
              className="amend-button"
            >
              Amend
            </button>
            <button
              onClick={() => handleUpdateValue(investment)}
              disabled={editableInvestment !== null && editableInvestment !== investment._id}
              className="update-value-button"
            >
              Update Value
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default InvestmentItem;
